import { BASE_HP, HitZone, ZONE_MULTIPLIER, type FireKind, type MatchWeaponConfig, type WeaponDef, type WeaponTuning } from '@vm/shared';
import { STARTER_LOADOUT, WEAPONS } from './weapons';

/**
 * Live weapon balance (admin `weapons.balance`). A match gets its own WeaponTable built from the
 * settings it was created with, so the game server, bots and every client in the match agree on the
 * numbers; matches already running keep the values they started with.
 */

export type TuningGroup = 'Damage' | 'Fire rate & ammo' | 'Accuracy & range' | 'Mobility' | 'Projectile' | 'Explosion' | 'Beam' | 'Melee' | 'Destruction';

export interface WeaponTuningField {
  /** Override key in WeaponTuning. */
  key: string;
  label: string;
  group: TuningGroup;
  unit?: string;
  min: number;
  max: number;
  int?: boolean;
  /** Location on WeaponDef. */
  path: readonly string[];
  /** Weapon kinds the stat means something for (default: any weapon that has it). */
  kinds?: readonly FireKind[];
  /** The value the game uses when the definition leaves the field out. */
  fallback?: (w: WeaponDef) => number;
  /** A base value of 0 still means something (spread, recoil...); otherwise 0 = not used by this weapon. */
  zeroOk?: boolean;
  hint?: string;
}

const AIMED: readonly FireKind[] = ['hitscan', 'projectile'];

export const WEAPON_TUNING_FIELDS: readonly WeaponTuningField[] = [
  { key: 'damage', label: 'Damage per hit', group: 'Damage', min: 0, max: 1000, path: ['damage'], kinds: ['hitscan', 'melee'], hint: 'Per bullet or pellet; per swing for melee.' },
  { key: 'pellets', label: 'Pellets', group: 'Damage', min: 1, max: 30, int: true, path: ['pellets'] },
  { key: 'headMult', label: 'Headshot multiplier', group: 'Damage', unit: '×', min: 1, max: 5, path: ['headMult'], kinds: AIMED, fallback: () => ZONE_MULTIPLIER[HitZone.Head] },
  { key: 'falloffMin', label: 'Damage at long range', group: 'Damage', unit: '×', min: 0, max: 1, path: ['falloffMin'], zeroOk: true, hint: 'Damage multiplier at the falloff end and beyond.' },
  { key: 'rpm', label: 'Fire rate', group: 'Fire rate & ammo', unit: 'rpm', min: 10, max: 3000, path: ['rpm'] },
  { key: 'mag', label: 'Magazine', group: 'Fire rate & ammo', min: 1, max: 500, int: true, path: ['mag'] },
  { key: 'reserve', label: 'Reserve ammo', group: 'Fire rate & ammo', min: 0, max: 5000, int: true, path: ['reserve'] },
  { key: 'reload', label: 'Reload time', group: 'Fire rate & ammo', unit: 's', min: 0.1, max: 10, path: ['reload'] },
  { key: 'charge', label: 'Charge time', group: 'Fire rate & ammo', unit: 's', min: 0, max: 5, path: ['charge'] },
  { key: 'equipTime', label: 'Equip time', group: 'Fire rate & ammo', unit: 's', min: 0.05, max: 3, path: ['equipTime'] },
  { key: 'spread', label: 'Hip-fire spread', group: 'Accuracy & range', unit: '°', min: 0, max: 30, path: ['spread'], kinds: AIMED, zeroOk: true },
  { key: 'aimSpread', label: 'Aimed spread', group: 'Accuracy & range', unit: '°', min: 0, max: 30, path: ['aimSpread'], kinds: AIMED, zeroOk: true, fallback: (w) => w.spread * 0.5 },
  { key: 'moveSpread', label: 'Spread while moving', group: 'Accuracy & range', unit: '°', min: 0, max: 30, path: ['moveSpread'], kinds: AIMED, zeroOk: true, fallback: () => 0 },
  { key: 'recoil', label: 'Recoil', group: 'Accuracy & range', unit: '°', min: 0, max: 20, path: ['recoil'], kinds: AIMED, zeroOk: true },
  { key: 'range', label: 'Range', group: 'Accuracy & range', unit: 'm', min: 1, max: 1000, path: ['range'], kinds: ['hitscan'] },
  { key: 'falloffStart', label: 'Falloff starts', group: 'Accuracy & range', unit: 'm', min: 0, max: 1000, path: ['falloffStart'], zeroOk: true },
  { key: 'falloffEnd', label: 'Falloff ends', group: 'Accuracy & range', unit: 'm', min: 0, max: 1000, path: ['falloffEnd'] },
  { key: 'moveSpeed', label: 'Movement speed', group: 'Mobility', unit: '×', min: 0.5, max: 1.5, path: ['moveSpeed'] },
  { key: 'zoom', label: 'Aim zoom (FOV)', group: 'Mobility', unit: '×', min: 0.2, max: 1, path: ['zoom'], hint: 'Field of view while aiming; lower zooms in more.' },
  { key: 'projectile.speed', label: 'Projectile speed', group: 'Projectile', unit: 'm/s', min: 1, max: 500, path: ['projectile', 'speed'] },
  { key: 'projectile.gravity', label: 'Projectile gravity', group: 'Projectile', unit: '×', min: 0, max: 5, path: ['projectile', 'gravity'], zeroOk: true },
  { key: 'projectile.impactDamage', label: 'Direct-hit damage', group: 'Projectile', min: 0, max: 1000, path: ['projectile', 'impactDamage'], zeroOk: true },
  { key: 'projectile.fuse', label: 'Fuse', group: 'Projectile', unit: 's', min: 0.1, max: 30, path: ['projectile', 'fuse'] },
  { key: 'projectile.burn', label: 'Burn damage', group: 'Projectile', unit: '/s', min: 0, max: 200, path: ['projectile', 'burn'] },
  { key: 'explosion.damage', label: 'Blast damage', group: 'Explosion', min: 0, max: 1000, path: ['projectile', 'explosion', 'damage'], hint: 'At the center; 25% at the edge.' },
  { key: 'explosion.radius', label: 'Blast radius', group: 'Explosion', unit: 'm', min: 0.5, max: 20, path: ['projectile', 'explosion', 'radius'] },
  { key: 'explosion.knockback', label: 'Knockback', group: 'Explosion', unit: 'm/s', min: 0, max: 60, path: ['projectile', 'explosion', 'knockback'], zeroOk: true },
  { key: 'explosion.selfDamage', label: 'Self damage', group: 'Explosion', unit: '×', min: 0, max: 2, path: ['projectile', 'explosion', 'selfDamage'], zeroOk: true },
  { key: 'explosion.voxelPower', label: 'Crater strength', group: 'Explosion', min: 0, max: 200, path: ['projectile', 'explosion', 'voxelPower'], zeroOk: true },
  { key: 'explosion.voxelRadius', label: 'Crater radius', group: 'Explosion', unit: 'm', min: 0, max: 8, path: ['projectile', 'explosion', 'voxelRadius'], zeroOk: true },
  { key: 'beam.dps', label: 'Beam damage', group: 'Beam', unit: '/s', min: 0, max: 2000, path: ['beam', 'dps'], zeroOk: true },
  { key: 'beam.range', label: 'Beam range', group: 'Beam', unit: 'm', min: 1, max: 200, path: ['beam', 'range'] },
  { key: 'beam.voxelDps', label: 'Beam digging', group: 'Beam', unit: '/s', min: 0, max: 2000, path: ['beam', 'voxelDps'], zeroOk: true },
  { key: 'beam.burn', label: 'Beam burn damage', group: 'Beam', unit: '/s', min: 0, max: 200, path: ['beam', 'burn'] },
  { key: 'melee.range', label: 'Melee reach', group: 'Melee', unit: 'm', min: 0.5, max: 10, path: ['melee', 'range'] },
  { key: 'melee.windup', label: 'Swing windup', group: 'Melee', unit: 's', min: 0, max: 2, path: ['melee', 'windup'], zeroOk: true },
  { key: 'melee.voxelPower', label: 'Swing digging', group: 'Melee', min: 0, max: 200, path: ['melee', 'voxelPower'], zeroOk: true },
  { key: 'voxelPower', label: 'Block damage per hit', group: 'Destruction', min: 0, max: 200, path: ['voxelPower'], kinds: AIMED, zeroOk: true },
  { key: 'voxelRadius', label: 'Impact crater radius', group: 'Destruction', unit: 'm', min: 0, max: 4, path: ['voxelRadius'], kinds: ['hitscan'], zeroOk: true },
  { key: 'penetration', label: 'Wall penetration', group: 'Destruction', min: 0, max: 500, path: ['penetration'], kinds: ['hitscan'] },
];

export const WEAPON_TUNING_FIELD_BY_KEY: Readonly<Record<string, WeaponTuningField>> = Object.fromEntries(WEAPON_TUNING_FIELDS.map((f) => [f.key, f]));

function readPath(o: unknown, path: readonly string[]): unknown {
  let v = o;
  for (const k of path) {
    if (!v || typeof v !== 'object') return undefined;
    v = (v as Record<string, unknown>)[k];
  }
  return v;
}

function writePath(o: object, path: readonly string[], value: number): void {
  let t = o as Record<string, unknown>;
  for (const k of path.slice(0, -1)) t = t[k] as Record<string, unknown>;
  t[path[path.length - 1]] = value;
}

/** The weapon's built-in value for a stat, or undefined when the stat doesn't apply to it. */
export function tuningBaseValue(w: WeaponDef, f: WeaponTuningField): number | undefined {
  if (f.kinds && !f.kinds.includes(w.kind)) return undefined;
  const raw = readPath(w, f.path);
  const v = typeof raw === 'number' ? raw : f.fallback && readPath(w, f.path.slice(0, -1)) !== undefined ? f.fallback(w) : undefined;
  if (v === undefined || (v === 0 && !f.zeroOk)) return undefined;
  return v;
}

/** Stats an admin can tune on this weapon. */
export function tuningFieldsFor(w: WeaponDef): WeaponTuningField[] {
  return WEAPON_TUNING_FIELDS.filter((f) => tuningBaseValue(w, f) !== undefined);
}

/** Problems with one weapon's overrides (unknown stat, wrong weapon, out of range); empty when valid. */
export function tuningProblems(w: WeaponDef, t: WeaponTuning): string[] {
  const out: string[] = [];
  for (const [key, v] of Object.entries(t)) {
    const f = WEAPON_TUNING_FIELD_BY_KEY[key];
    if (!f) out.push(`${w.name}: unknown stat "${key}"`);
    else if (tuningBaseValue(w, f) === undefined) out.push(`${w.name} has no ${f.label.toLowerCase()}`);
    else if (typeof v !== 'number' || !Number.isFinite(v) || v < f.min || v > f.max) out.push(`${w.name}: ${f.label.toLowerCase()} must be ${f.min}..${f.max}`);
    else if (f.int && !Number.isInteger(v)) out.push(`${w.name}: ${f.label.toLowerCase()} must be a whole number`);
  }
  const start = t.falloffStart ?? w.falloffStart;
  const end = t.falloffEnd ?? w.falloffEnd;
  if (start !== undefined && end !== undefined && start > end) out.push(`${w.name}: falloff must start before it ends`);
  return out;
}

/** A copy of the weapon with the overrides applied (unknown or inapplicable keys are ignored, values clamped). */
export function tunedWeapon(w: WeaponDef, t: WeaponTuning | undefined): WeaponDef {
  if (!t) return w;
  let out: WeaponDef | null = null;
  for (const [key, raw] of Object.entries(t)) {
    const f = WEAPON_TUNING_FIELD_BY_KEY[key];
    if (!f || typeof raw !== 'number' || !Number.isFinite(raw) || tuningBaseValue(w, f) === undefined) continue;
    let v = Math.min(f.max, Math.max(f.min, raw));
    if (f.int) v = Math.round(v);
    out ??= structuredClone(w);
    writePath(out, f.path, v);
  }
  return out ?? w;
}

/** Weapons that can be taken out of play: shop weapons except the hammer every loadout falls back to. */
export function canDisableWeapon(w: WeaponDef | undefined): boolean {
  return !!w && !w.hidden && w.id !== 'builders_hammer';
}

export interface WeaponTable {
  readonly list: readonly WeaponDef[];
  readonly byId: Readonly<Record<string, WeaponDef>>;
  /** Indexed by wire `num`. */
  readonly byNum: readonly WeaponDef[];
  /** Purchasable weapons still in play. */
  readonly shop: readonly WeaponDef[];
  readonly disabled: ReadonlySet<string>;
  /** True when this table differs from the built-in weapons. */
  readonly tuned: boolean;
  /** The weapon when it exists and is in play. */
  get(id: string): WeaponDef | undefined;
  /** Stand-in for a disabled weapon: the slot's starter weapon, else the earliest unlock of the slot still in play. */
  fallback(slot: number): WeaponDef | null;
}

function makeTable(list: WeaponDef[], disabled: Set<string>): WeaponTable {
  const byId: Record<string, WeaponDef> = Object.fromEntries(list.map((w) => [w.id, w]));
  const byNum: WeaponDef[] = [];
  for (const w of list) byNum[w.num] = w;
  const shop = list.filter((w) => !w.hidden && !disabled.has(w.id));
  const fallbacks = new Map<number, WeaponDef | null>();
  return {
    list,
    byId,
    byNum,
    shop,
    disabled,
    tuned: list.some((w, i) => w !== WEAPONS[i]) || disabled.size > 0,
    get: (id) => (disabled.has(id) ? undefined : byId[id]),
    fallback: (slot) => {
      if (!fallbacks.has(slot)) {
        const starter = byId[STARTER_LOADOUT.weapons[slot] ?? ''];
        const pick =
          starter && starter.slot === slot && !disabled.has(starter.id)
            ? starter
            : ([...shop].filter((w) => w.slot === slot).sort((a, b) => a.unlockLevel - b.unlockLevel || a.price - b.price)[0] ?? null);
        fallbacks.set(slot, pick);
      }
      return fallbacks.get(slot)!;
    },
  };
}

/** The built-in weapons, untuned. */
export const BASE_WEAPON_TABLE: WeaponTable = makeTable(WEAPONS, new Set());

/** Weapons for a match: overrides applied, disabled weapons marked (never the hidden mode tools or the hammer). */
export function weaponTable(cfg: MatchWeaponConfig | null | undefined): WeaponTable {
  const overrides = cfg?.overrides ?? {};
  const disabled = new Set((cfg?.disabled ?? []).filter((id) => canDisableWeapon(BASE_WEAPON_TABLE.byId[id])));
  if (!Object.keys(overrides).length && !disabled.size) return BASE_WEAPON_TABLE;
  return makeTable(
    WEAPONS.map((w) => tunedWeapon(w, overrides[w.id])),
    disabled,
  );
}

/** Damage numbers for balance previews (no armor, body shots). */
export interface WeaponDamageStats {
  /** Damage of one shot/swing (all pellets; direct hit + blast for explosives; per second for beams). */
  perShot: number;
  /** Damage per second while firing. */
  dps: number;
  /** Including reloads. */
  sustainedDps: number;
  /** Shots/swings to take down a full-health marine (beams: seconds). */
  toKill: number;
}

export function weaponDamageStats(w: WeaponDef, hp = BASE_HP): WeaponDamageStats {
  const rps = w.rpm / 60;
  const srps = w.mag > 0 ? w.mag / (w.mag / rps + w.reload) : rps;
  if (w.beam) return { perShot: w.beam.dps, dps: w.beam.dps, sustainedDps: w.beam.dps, toKill: w.beam.dps > 0 ? hp / w.beam.dps : Infinity };
  const perShot = w.projectile ? w.projectile.impactDamage + (w.projectile.explosion?.damage ?? 0) : w.damage * (w.pellets ?? 1);
  return { perShot, dps: perShot * rps, sustainedDps: perShot * srps, toKill: perShot > 0 ? Math.ceil(hp / perShot) : Infinity };
}
